import { useAuthStore } from '@/store/authStore';
import { CustomerDashboardPage } from './CustomerDashboardPage';
import { ProviderDashboardPage } from './ProviderDashboardPage';
import { CompanyDashboardPage } from './CompanyDashboardPage';
import { AdminDashboardPage } from './AdminDashboardPage';
import { Cpu, Activity, AlertTriangle } from 'lucide-react';
import { SkipLink } from '@/components/ui/SkipLink';

export function DashboardPage() {
  const { user, isLoading, isInitialized } = useAuthStore();

  // Wait for auth to be initialized before routing
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <div className="absolute inset-0 bg-gray-50 z-0"></div>
        
        <main id="main-content" role="main" className="relative z-10 text-center">
          <div className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mx-auto mb-8 shadow-sm" />
          <h2 className="text-xl font-bold text-gray-900 tracking-wide mb-2">Loading Dashboard</h2>
          <p className="text-gray-600 text-sm animate-pulse">Please wait...</p>
        </main>
      </div>
    );
  }

  // This page is already protected by ProtectedRoute, so user should always exist after initialization
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <div className="absolute inset-0 bg-gray-50 z-0"></div>
        
        <main id="main-content" role="main" className="relative z-10 text-center">
          <div className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mx-auto mb-8 shadow-sm" />
          <h2 className="text-xl font-bold text-gray-900 tracking-wide mb-2">Loading Dashboard</h2>
          <p className="text-gray-600 text-sm animate-pulse">Please wait...</p>
        </main>
      </div>
    );
  }

  // Route to appropriate dashboard based on user role
  if (user.role === 'customer') {
    return <CustomerDashboardPage />;
  }

  if (user.role === 'provider' || user.role === 'company') {
    // Check if this is a company provider
    const providerType = (user as any).providerType;
    if (providerType === 'COMPANY' || providerType === 'company') {
      return <CompanyDashboardPage />;
    }
    // Individual providers get the regular provider dashboard
    return <ProviderDashboardPage />;
  }

  if (user.role === 'admin' || user.role === 'support') {
    return <AdminDashboardPage />;
  }

  // Fallback for unknown roles
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <div className="absolute inset-0 bg-gray-50 z-0"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent"></div>
      

      <main id="main-content" role="main" className="relative z-10 bg-amber-50 border border-amber-200 p-12 rounded-2xl text-center max-w-xl shadow-lg">
        <div className="w-20 h-20 bg-gray-50 border-2 border-amber-500 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-sm rotate-3">
          <AlertTriangle className="w-10 h-10 text-amber-500" />
        </div>
        <h1 className="text-4xl font-black text-white mb-6 tracking-tighter uppercase italic">
          ROLE_MISMATCH_ERROR
        </h1>
        <p className="text-gray-500 font-mono text-xs leading-relaxed uppercase tracking-[0.2em] mb-12">
          Your current authorization profile [ {(user.role as string)?.toUpperCase() || 'NONE'} ] does not match any known sectoral command structures.
        </p>

        <button
          onClick={() => window.location.href = '/'}
          className="px-12 py-5 bg-white/5 border border-white/10 text-white font-mono text-[11px] font-black uppercase tracking-[0.4em] rounded-2xl hover:bg-white/10 transition-all flex items-center justify-center gap-4 mx-auto min-h-touch"
          aria-label="Return to home page"
        >
          <Activity className="w-5 h-5 text-primary-500" />
          RETURN_TO_BASE_Message
        </button>
      </main>
    </div>
  );
}
